import clsx from "clsx";
import { FC, ReactNode } from "react";

import { Icon, IconName } from "@tsmono/react/icons";

import styles from "./ToolBlock.module.css";

interface ToolBlockProps {
  id?: string;
  /** Icon for the tool kind (terminal = client, globe = server). */
  icon: IconName;
  /** Tool name, rendered in monospace with the tool accent color. */
  title: string;
  /** Single-line args summary; ellipsized, never wraps. */
  summary?: string;
  /** Optional neutral pill after the title (e.g. "server"). */
  pill?: string;
  /** Flush rows (server calls inside the assistant turn) carry no container
   * border of their own — the turn container frames them. */
  flush?: boolean;
  className?: string | string[];
  /** Optional leading element rendered before the tool icon (e.g. a
   *  selection checkbox while evidence selection mode is on). */
  headerLeading?: ReactNode;
  children?: ReactNode;
}

/**
 * The shared tool-call block grammar: a tinted header row (tool icon · mono
 * tool name · optional pill · args summary) with the tool's input/output
 * zones stacked beneath it.
 */
export const ToolBlock: FC<ToolBlockProps> = ({
  id,
  icon,
  title,
  summary,
  pill,
  flush,
  className,
  headerLeading,
  children,
}) => {
  return (
    <div
      id={id}
      className={clsx(
        styles.block,
        flush ? undefined : styles.standalone,
        className
      )}
    >
      <div className={styles.header}>
        {headerLeading}
        <Icon name={icon} className={styles.icon} />
        <span className={styles.title}>{title}</span>
        {summary ? <span className={styles.summary}>{summary}</span> : null}
        {pill ? <span className={styles.pill}>{pill}</span> : null}
      </div>
      {children}
    </div>
  );
};

/** Input zone (e.g. code) — code fill, hairline top. */
export const ToolBlockInput: FC<{
  className?: string | string[];
  children?: ReactNode;
}> = ({ className, children }) => {
  return <div className={clsx(styles.inputZone, className)}>{children}</div>;
};

/** Output well — faint fill, hairline top; content is whatever the tool
 * returned, rendered by the caller. */
export const ToolBlockOutput: FC<{
  className?: string | string[];
  children?: ReactNode;
}> = ({ className, children }) => {
  return <div className={clsx(styles.outputWell, className)}>{children}</div>;
};

/** A region that holds tool call checks: tinted like the input zone before
 * the call, like the output well after it. */
export const ToolBlockInset: FC<{
  region: "input" | "output";
  children?: ReactNode;
}> = ({ region, children }) => {
  return (
    <div
      className={
        region === "input" ? styles.inputInsetZone : styles.outputInsetZone
      }
    >
      {children}
    </div>
  );
};
